/**
 * 애니메이션 WebP 파일의 총 재생 시간(초)을 ANMF 청크 파싱으로 계산한다.
 * make-item-wkwk.ts의 isAnimatedWebp() 바이너리 파싱 로직을 확장.
 *
 * ANMF 청크: offset+12~14에 24-bit LE duration(ms) 필드.
 * 모든 ANMF의 duration 합산 → seconds 변환.
 */

const FALLBACK_DURATION_SECONDS = 2;

const readFourCC = (view: DataView, offset: number): string => {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
};

const WEBP_ANIMATION_FLAG = 0b00000010;

/**
 * WebP 파일이 애니메이션인지 확인한다.
 * VP8X 확장 헤더의 animation 플래그 또는 ANIM/ANMF 청크 존재 여부로 판별.
 */
export const isAnimatedWebp = async (file: Blob): Promise<boolean> => {
  const buffer = await file.arrayBuffer();

  if (buffer.byteLength < 16) {
    return false;
  }

  const view = new DataView(buffer);
  const riff = readFourCC(view, 0);
  const webp = readFourCC(view, 8);

  if (riff !== 'RIFF' || webp !== 'WEBP') {
    return false;
  }

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunkId = readFourCC(view, offset);
    const chunkSize = view.getUint32(offset + 4, true);
    const chunkDataOffset = offset + 8;

    if (chunkDataOffset > view.byteLength) {
      break;
    }

    if (chunkId === 'ANIM' || chunkId === 'ANMF') {
      return true;
    }

    if (
      chunkId === 'VP8X' &&
      chunkSize >= 1 &&
      chunkDataOffset + 1 <= view.byteLength
    ) {
      const flags = view.getUint8(chunkDataOffset);
      if ((flags & WEBP_ANIMATION_FLAG) !== 0) {
        return true;
      }
    }

    if (chunkDataOffset + chunkSize > view.byteLength) {
      break;
    }

    const paddedChunkSize = chunkSize + (chunkSize % 2);
    offset = chunkDataOffset + paddedChunkSize;
  }

  return false;
};

export const getWebpDurationInSeconds = async (file: Blob): Promise<number> => {
  const buffer = await file.arrayBuffer();

  if (buffer.byteLength < 16) {
    return FALLBACK_DURATION_SECONDS;
  }

  const view = new DataView(buffer);
  const riff = readFourCC(view, 0);
  const webp = readFourCC(view, 8);

  if (riff !== 'RIFF' || webp !== 'WEBP') {
    return FALLBACK_DURATION_SECONDS;
  }

  let totalDurationMs = 0;
  let offset = 12;

  while (offset + 8 <= view.byteLength) {
    const chunkId = readFourCC(view, offset);
    const chunkSize = view.getUint32(offset + 4, true);
    const chunkDataOffset = offset + 8;

    if (chunkDataOffset > view.byteLength) {
      break;
    }

    // ANMF 청크의 duration은 offset+12 위치에 24-bit LE로 저장
    if (chunkId === 'ANMF' && chunkSize >= 16) {
      const durationOffset = chunkDataOffset + 12;
      if (durationOffset + 3 <= view.byteLength) {
        const durationMs =
          view.getUint8(durationOffset) |
          (view.getUint8(durationOffset + 1) << 8) |
          (view.getUint8(durationOffset + 2) << 16);
        totalDurationMs += durationMs;
      }
    }

    if (chunkDataOffset + chunkSize > view.byteLength) {
      break;
    }

    const paddedChunkSize = chunkSize + (chunkSize % 2);
    offset = chunkDataOffset + paddedChunkSize;
  }

  if (totalDurationMs === 0) {
    return FALLBACK_DURATION_SECONDS;
  }

  return totalDurationMs / 1000;
};
